'use client';

import React, { useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import { FileRetrieveResponse } from '@types';
import { cn, FileUploadItem } from '@utils';
import { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';

import { LoadingSpinner } from '../LoadingSpinner';
import { NextButton, PrevButton, usePrevNextButtons } from './CarouselArrowButtons';
import { DotButton, useDotButton } from './CarouselDotButton';

type ImageCarouselProps = {
	printLabel?: string;
	/** Array of FileUploadItem objects representing the files to be displayed in the carousel */
	files: FileUploadItem[];
	/** Function to update the files array */
	updateFiles?: (updatedFiles: FileUploadItem[]) => void;
	/** Function to retrieve a file given its entity ID and file URI */
	retrieveFile?: (fileIdentifier: string) => Promise<FileRetrieveResponse>;
	/** Optional configuration options for the Embla Carousel */
	options?: EmblaOptionsType;
};

export const ImageCarousel: React.FC<ImageCarouselProps> = props => {
	const { files, updateFiles, retrieveFile, options } = props;
	const [emblaRef, emblaApi] = useEmblaCarousel(options);

	const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(emblaApi);
	const { prevBtnDisabled, nextBtnDisabled, onPrevButtonClick, onNextButtonClick } =
		usePrevNextButtons(emblaApi);
	const [imageErrors, setImageErrors] = useState<Record<string, boolean>>({});
	const [imageLoading, setImageLoading] = useState<Record<string, boolean>>({});

	const imageSources = useMemo(() => {
		return files
			.map(file => ({
				filename: file.file?.name || file.filename || '',
				src: file.file ? URL.createObjectURL(file.file) : (file.base64 ?? ''),
			}))
			.filter(item => item.src !== '');
	}, [files]);

	const fetchImage = async (file: FileUploadItem) => {
		const filename = file.file?.name || file.filename || '';
		if (file.base64) return file;

		setImageLoading(prev => ({ ...prev, [filename]: true }));
		try {
			if (file.file) {
				const base64 = await fileToBase64(file.file);
				return { ...file, base64 };
			}

			if (typeof retrieveFile === 'function' && file.fileLocation?.identifier) {
				const { content } = await retrieveFile(file.fileLocation.identifier);
				return {
					...file,
					base64: `data:image/${filename.split('.').pop()};base64,${content}`,
				};
			} else {
				setImageErrors(prev => ({ ...prev, [filename]: true }));
			}
			return file;
		} catch (error) {
			console.error(`Failed to fetch image for ${filename}:`, error);
			setImageErrors(prev => ({ ...prev, [filename]: true }));
			return file;
		} finally {
			setImageLoading(prev => ({ ...prev, [filename]: false }));
		}
	};

	useEffect(() => {
		const fetchImages = async () => {
			// Filter files that need fetching - not already fetched and not already failed
			const filesToFetch = files.filter(file => {
				const filename = file.file?.name || file.filename || '';
				return !file.base64 && !imageErrors[filename];
			});

			// If there are files to fetch and we can update files
			if (filesToFetch.length > 0 && updateFiles) {
				// Process all files, fetching only those that need it
				const updatedFiles = await Promise.all(
					files.map(async file => {
						const filename = file.file?.name || file.filename || '';
						return file.base64 || imageErrors[filename] ? file : await fetchImage(file);
					}),
				);

				updateFiles(updatedFiles);
			}
		};

		fetchImages();

		// Clear errors and loading state for removed files
		setImageErrors(prev => {
			const newErrors = { ...prev };
			Object.keys(newErrors).forEach(filename => {
				if (!files.some(file => (file.file?.name || file.filename) === filename)) {
					delete newErrors[filename];
				}
			});
			return newErrors;
		});

		setImageLoading(prev => {
			const newLoading = { ...prev };
			Object.keys(newLoading).forEach(filename => {
				if (!files.some(file => (file.file?.name || file.filename) === filename)) {
					delete newLoading[filename];
				}
			});
			return newLoading;
		});

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [files]);

	const fileToBase64 = (file: File): Promise<string> => {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();
			reader.readAsDataURL(file);
			reader.onload = () => resolve(reader.result as string);
			reader.onerror = error => reject(error);
		});
	};

	const ImageCard = ({
		src,
		alt,
		loading,
		error,
		filename,
		onError,
	}: {
		src?: string;
		alt: string;
		loading: boolean;
		error: boolean;
		filename?: string;
		onError: () => void;
	}) => {
		return (
			<div className="border-light-border relative flex h-72 items-center justify-center rounded-xs border">
				{loading ? (
					<LoadingSpinner size="large" />
				) : error ? (
					<p>Failed to load image</p>
				) : src ? (
					<>
						<Image
							fill
							src={src}
							alt={alt}
							className="rounded-xs object-contain"
							onError={onError}
						/>
						{filename && (
							<div
								className={cn(
									'text-neutral-white text-center text-xs text-ellipsis whitespace-nowrap',
									'bg-neutral-black/40 absolute right-0 bottom-0 left-0 overflow-hidden rounded-b-xs px-4 py-1.5',
								)}
							>
								{filename}
							</div>
						)}
					</>
				) : null}
			</div>
		);
	};

	const CarouselControls = () => {
		if (files.length <= 1) return null;

		return (
			<div className="mt-7 grid grid-cols-[auto_1fr] justify-between gap-3">
				<div className="grid grid-cols-2 items-center gap-2">
					<PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
					<NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
				</div>

				{/** Dot indicators */}
				<div className="flex flex-wrap items-center justify-end gap-1">
					{scrollSnaps.map((_, index) => {
						const isSelected = index === selectedIndex;

						return (
							<DotButton
								key={index}
								onClick={e => {
									e.preventDefault();
									e.stopPropagation();
									onDotButtonClick(index);
								}}
								className={cn(
									'bg-light-background-alt-shade size-3 cursor-pointer touch-manipulation rounded-full border-0',
									isSelected && 'border-light-action-secondary cursor-default border-1',
								)}
							/>
						);
					})}
				</div>
			</div>
		);
	};

	/* Report layout: print one image per page */
	const ReportLayout = () => {
		return (
			<section className="hidden print:block">
				{files.map((file, index) => {
					const filename = file.file?.name || file.filename || '';
					const hasImage = !!imageSources[index]?.src;
					const hasError = imageErrors[filename];

					// Don't render anything if no image and no error
					if (!hasImage && !hasError) return null;

					return (
						<div
							key={`report-photo-${index}`}
							className={cn('h-auto print:flex print:break-after-page print:flex-col')}
						>
							{hasError ? (
								<div className="mt-32 text-lg">Failed to load image</div>
							) : hasImage ? (
								<>
									<h4 className="mb-6">{props.printLabel}</h4>
									{/* eslint-disable-next-line @next/next/no-img-element */}
									<img
										src={imageSources[index].src}
										alt={`Uploaded image ${filename}`}
										className="mb-4 h-auto max-h-[80vh] w-full object-fill"
									/>
									{imageSources[index].filename && (
										<p className="text-light-text-lighter text-center">
											{imageSources[index].filename}
										</p>
									)}
								</>
							) : null}
						</div>
					);
				})}
			</section>
		);
	};

	if (files.length === 0) {
		return null;
	}

	return (
		<>
			<section className="mx-auto max-w-192 pr-4 print:hidden">
				<div className="overflow-hidden" ref={emblaRef}>
					<div className="backface-visible-hidden -ml-4 flex touch-pan-y touch-pinch-zoom">
						{files.map((file, index) => {
							const fileName = file.file?.name || file.filename || '';
							return (
								<div key={index} className="min-w-0 flex-[0_0_70%] pl-4">
									<ImageCard
										src={imageSources[index]?.src}
										alt={`Uploaded image ${fileName}`}
										loading={!!imageLoading[fileName]}
										error={!!imageErrors[fileName]}
										filename={imageSources[index]?.filename}
										onError={() =>
											setImageErrors(prev => ({
												...prev,
												[fileName]: true,
											}))
										}
									/>
								</div>
							);
						})}
					</div>
				</div>
				<CarouselControls />
			</section>
			<ReportLayout />
		</>
	);
};
